import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ScaleGrowthSvg } from './ScaleGrowthSvg';

import './index.scss';

type ScaleGrowthProps = {
  animationEnabled?: boolean;
  skipAnimation?: boolean;
};

// 阻尼系数
const damping = 0.09;
// 停止动画阈值
const deltaThreshold = 0.01;

const ScaleGrowth: React.FC<ScaleGrowthProps> = ({ animationEnabled, skipAnimation }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [rotation, setRotation] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [showCursorEvents, setShowCursorEvents] = useState(false);

  // 目标旋转角度和当前旋转角度
  const targetRotation = useRef(0);
  const currentRotation = useRef(0);
  const animationRef = useRef<number>();

  // 4400ms（容器移动到原位置后） 展示 cursor-events 容器
  useEffect(() => {
    if (skipAnimation) {
      setShowCursorEvents(true);
      return;
    }
    const timer = window.setTimeout(() => {
      setShowCursorEvents(true);
    }, 4400);

    return () => {
      window.clearTimeout(timer);
    };
  }, [skipAnimation]);

  useEffect(() => {
    const animate = () => {
      // 计算差值并应用阻尼
      const diff = targetRotation.current - currentRotation.current;
      currentRotation.current += diff * damping;

      // 当差值足够小时停止更新，避免不必要的渲染
      if (Math.abs(diff) > deltaThreshold) {
        setRotation(currentRotation.current);
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  /**
   * 处理鼠标移动事件
   */
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const centerY = rect.top + rect.height / 2;

    // 计算鼠标相对于垂直中心的位置
    const y = (e.clientY - centerY) / (rect.height / 2);

    // 限制最大旋转角度为正负7度
    const maxRotation = 7;
    const clampedY = Math.max(-1, Math.min(1, y));
    targetRotation.current = clampedY * maxRotation;
  }, []);

  /**
   * 处理鼠标进入事件
   */
  const handleMouseEnter = useCallback(() => {
    setIsHovering(true);
  }, []);

  /**
   * 处理鼠标离开事件
   */
  const handleMouseLeave = useCallback(() => {
    setIsHovering(false);
    targetRotation.current = 0;
  }, []);

  return (
    <div className={classNames('odc-scale-growth', { 'animation-enabled': animationEnabled }, { 'skip-animation': skipAnimation })} ref={containerRef}>
      <div className="scale-growth-acquire"><div>Driving Profitable Growth.</div></div>
      <div className="scale-growth-scale"><div>Built around Clients. Powered by AI.</div></div>
      <div
        className="scale-growth-svg-wrapper"
        style={{
          transform: `rotate(${rotation}deg)`,
        }}
      >
        <div className="scale-growth-svg">
          <ScaleGrowthSvg />
        </div>
      </div>
      {/* 当.odc-scale-growth 动画初始动画结束后，展示这个容器 */}
      {showCursorEvents && <div
        className="scale-growth-cursor-events"
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      ></div>}
    </div>
  );
};

export default ScaleGrowth;